import crypto from "crypto";
import fs, { existsSync, readFileSync, watchFile } from "fs";
import path, { dirname, join } from "path";
import { fileURLToPath } from "url";
import yaml from "js-yaml";
class RateLimiter {
  attempts = /* @__PURE__ */ new Map();
  maxAttempts;
  windowMs;
  blockDurationMs;
  cleanupIntervalMs = 6e4;
  // Clean up old entries every minute
  cleanupTimer;
  constructor(options = {}) {
    this.maxAttempts = options.maxAttempts || 5;
    this.windowMs = options.windowMs || 15 * 60 * 1e3;
    this.blockDurationMs = options.blockDurationMs || 30 * 60 * 1e3;
    this.cleanupTimer = setInterval(() => this.cleanup(), this.cleanupIntervalMs);
  }
  /**
   * Check if an identifier (IP, username, etc.) should be rate limited
   * @returns true if the request should be blocked, false if allowed
   */
  isBlocked(identifier) {
    const entry = this.attempts.get(identifier);
    if (!entry) {
      return false;
    }
    if (entry.blocked && entry.blockExpiry) {
      if (Date.now() < entry.blockExpiry) {
        return true;
      } else {
        entry.blocked = false;
        entry.blockExpiry = void 0;
        entry.attempts = 0;
      }
    }
    if (Date.now() - entry.firstAttempt > this.windowMs) {
      entry.attempts = 0;
      entry.firstAttempt = Date.now();
      return false;
    }
    return entry.attempts >= this.maxAttempts;
  }
  /**
   * Record a failed attempt
   */
  recordFailedAttempt(identifier) {
    let entry = this.attempts.get(identifier);
    if (!entry) {
      entry = {
        attempts: 1,
        firstAttempt: Date.now(),
        lastAttempt: Date.now(),
        blocked: false
      };
      this.attempts.set(identifier, entry);
    } else {
      entry.attempts++;
      entry.lastAttempt = Date.now();
      if (entry.attempts >= this.maxAttempts) {
        entry.blocked = true;
        entry.blockExpiry = Date.now() + this.blockDurationMs;
        console.warn(`[RateLimiter] Blocking ${identifier} for ${this.blockDurationMs}ms after ${entry.attempts} failed attempts`);
      }
    }
  }
  /**
   * Record a successful attempt (resets the counter)
   */
  recordSuccessfulAttempt(identifier) {
    this.attempts.delete(identifier);
  }
  /**
   * Get remaining attempts for an identifier
   */
  getRemainingAttempts(identifier) {
    const entry = this.attempts.get(identifier);
    if (!entry) {
      return this.maxAttempts;
    }
    if (Date.now() - entry.firstAttempt > this.windowMs) {
      return this.maxAttempts;
    }
    return Math.max(0, this.maxAttempts - entry.attempts);
  }
  /**
   * Clean up old entries to prevent memory leaks
   */
  cleanup() {
    const now = Date.now();
    const entriesToDelete = [];
    for (const [identifier, entry] of this.attempts.entries()) {
      if (now - entry.lastAttempt > 36e5) {
        entriesToDelete.push(identifier);
      }
    }
    for (const identifier of entriesToDelete) {
      this.attempts.delete(identifier);
    }
    if (entriesToDelete.length > 0) {
      console.log(`[RateLimiter] Cleaned up ${entriesToDelete.length} old entries`);
    }
  }
  /**
   * Destroy the rate limiter and clean up resources
   */
  destroy() {
    if (this.cleanupTimer) {
      clearInterval(this.cleanupTimer);
    }
    this.attempts.clear();
  }
}
const authRateLimiter = new RateLimiter({
  maxAttempts: 5,
  windowMs: 15 * 60 * 1e3,
  // 15 minutes
  blockDurationMs: 30 * 60 * 1e3
  // 30 minutes
});
class ConfigManager {
  config = {};
  configPath = null;
  constructor() {
    this.loadConfig();
  }
  loadConfig() {
    const possiblePaths = [
      path.join(process.cwd(), "morphbox.yml"),
      path.join(process.cwd(), "morphbox.yaml"),
      path.join(process.cwd(), ".morphbox.yml"),
      path.join(process.cwd(), ".morphbox.yaml")
    ];
    for (const configPath of possiblePaths) {
      if (fs.existsSync(configPath)) {
        try {
          const fileContents = fs.readFileSync(configPath, "utf8");
          this.config = yaml.load(fileContents) || {};
          this.configPath = configPath;
          console.log(`Loaded configuration from ${configPath}`);
          break;
        } catch (error) {
          console.warn(`Failed to parse config file ${configPath}:`, error);
        }
      }
    }
    if (!this.configPath) {
      console.log("No morphbox.yml configuration file found, using defaults");
    }
  }
  getConfig() {
    return this.config;
  }
  getPackages() {
    return this.config.container?.packages || [];
  }
  getEnvironment() {
    return this.config.container?.environment || {};
  }
  getAdditionalPorts() {
    return this.config.container?.ports || [];
  }
  getShell() {
    return this.config.container?.shell || "/bin/bash";
  }
  getAllowedDomains() {
    return this.config.network?.allowlist || null;
  }
  getBlockedDomains() {
    return this.config.network?.blocklist || [];
  }
  isNetworkDisabled() {
    return this.config.security?.no_network || false;
  }
  getMemoryLimit() {
    return this.config.security?.memory_limit || null;
  }
  getCpuLimit() {
    return this.config.security?.cpu_limit || null;
  }
  getDockerfileContent() {
    const packages = this.getPackages();
    const nodeVersion = this.config.development?.runtimes?.node;
    const pythonVersion = this.config.development?.runtimes?.python;
    const goVersion = this.config.development?.runtimes?.go;
    const npmPackages = this.config.development?.npm_packages || [];
    const pipPackages = this.config.development?.pip_packages || [];
    const goPackages = this.config.development?.go_packages || [];
    let dockerfile = `FROM ubuntu:22.04

ENV DEBIAN_FRONTEND=noninteractive
ENV TZ=UTC

RUN apt-get update && apt-get install -y \\
    sudo \\
    curl \\
    wget \\
    git \\
    build-essential \\
    software-properties-common \\
    ca-certificates \\
    gnupg \\
    lsb-release`;
    if (packages.length > 0) {
      dockerfile += ` \\
    ${packages.join(" \\\n    ")}`;
    }
    dockerfile += ` && \\
    apt-get clean && \\
    rm -rf /var/lib/apt/lists/*

# Create user
RUN useradd -m -s /bin/bash -u 1000 morphbox && \\
    echo "morphbox ALL=(ALL) NOPASSWD:ALL" >> /etc/sudoers`;
    if (nodeVersion) {
      dockerfile += `

# Install Node.js ${nodeVersion}
RUN curl -fsSL https://deb.nodesource.com/setup_${nodeVersion}.x | bash - && \\
    apt-get install -y nodejs`;
      if (npmPackages.length > 0) {
        dockerfile += ` && \\
    npm install -g ${npmPackages.join(" ")}`;
      }
    }
    if (pythonVersion) {
      dockerfile += `

# Install Python ${pythonVersion}
RUN add-apt-repository ppa:deadsnakes/ppa && \\
    apt-get update && \\
    apt-get install -y python${pythonVersion} python${pythonVersion}-pip`;
      if (pipPackages.length > 0) {
        dockerfile += ` && \\
    python${pythonVersion} -m pip install ${pipPackages.join(" ")}`;
      }
    }
    if (goVersion) {
      dockerfile += `

# Install Go ${goVersion}
RUN wget https://go.dev/dl/go${goVersion}.linux-amd64.tar.gz && \\
    tar -C /usr/local -xzf go${goVersion}.linux-amd64.tar.gz && \\
    rm go${goVersion}.linux-amd64.tar.gz
ENV PATH=$PATH:/usr/local/go/bin`;
      if (goPackages.length > 0) {
        dockerfile += `
RUN go install ${goPackages.join(" && go install ")}`;
      }
    }
    const env2 = this.getEnvironment();
    for (const [key, value] of Object.entries(env2)) {
      dockerfile += `
ENV ${key}="${value}"`;
    }
    if (this.config.scripts?.post_create) {
      dockerfile += `

# Run post-create script
RUN echo '${this.config.scripts.post_create.replace(/'/g, "'\\''")}' | bash`;
    }
    dockerfile += `

WORKDIR /workspace
USER morphbox`;
    return dockerfile;
  }
  getDockerRunArgs() {
    const args = [];
    const memLimit = this.getMemoryLimit();
    if (memLimit) {
      args.push("--memory", memLimit);
    }
    const cpuLimit = this.getCpuLimit();
    if (cpuLimit) {
      args.push("--cpus", cpuLimit.toString());
    }
    const ports = this.getAdditionalPorts();
    for (const port of ports) {
      args.push("-p", `${port}:${port}`);
    }
    if (this.isNetworkDisabled()) {
      args.push("--network", "none");
    }
    if (this.config.security?.readonly_root) {
      args.push("--read-only");
      args.push("--tmpfs", "/tmp");
      args.push("--tmpfs", "/var/tmp");
    }
    return args;
  }
  shouldAllowDomain(domain) {
    const allowlist = this.getAllowedDomains();
    const blocklist = this.getBlockedDomains();
    if (this.matchesDomainPattern(domain, blocklist)) {
      return false;
    }
    if (!allowlist || allowlist.length === 0) {
      return true;
    }
    return this.matchesDomainPattern(domain, allowlist);
  }
  matchesDomainPattern(domain, patterns) {
    for (const pattern of patterns) {
      if (pattern.startsWith("*.")) {
        const baseDomain = pattern.slice(2);
        if (domain === baseDomain || domain.endsWith("." + baseDomain)) {
          return true;
        }
      } else if (domain === pattern) {
        return true;
      }
    }
    return false;
  }
}
const __filename = fileURLToPath(import.meta.url);
const __dirname = dirname(__filename);
class AllowlistManager {
  config = {
    ipAllowlist: ["0.0.0.0/0"],
    // Allow all by default
    websiteAllowlist: [],
    portsAllowlist: []
  };
  configPath;
  fileWatcher = null;
  ipNets = [];
  yamlConfigManager = null;
  constructor() {
    try {
      this.yamlConfigManager = new ConfigManager();
      const yamlAllowlist = this.yamlConfigManager.getAllowedDomains();
      const yamlBlocklist = this.yamlConfigManager.getBlockedDomains();
      if (yamlAllowlist && yamlAllowlist.length > 0) {
        this.config.websiteAllowlist = yamlAllowlist;
        console.log("[AllowlistManager] Using website allowlist from morphbox.yml");
      }
    } catch (error) {
      console.log("[AllowlistManager] No YAML config found, checking for legacy config");
    }
    const possiblePaths = [
      join(process.cwd(), "morphbox-allowlist.conf"),
      join(dirname(process.cwd()), "morphbox-allowlist.conf"),
      join(__dirname, "../../../../morphbox-allowlist.conf"),
      "/etc/morphbox/allowlist.conf"
    ];
    this.configPath = possiblePaths.find((p) => existsSync(p)) || possiblePaths[0];
    if (!this.yamlConfigManager || !this.yamlConfigManager.getAllowedDomains()) {
      this.loadConfig();
    }
    this.watchConfig();
  }
  loadConfig() {
    try {
      if (!existsSync(this.configPath)) {
        console.log("[AllowlistManager] No config file found, using defaults");
        return;
      }
      const content = readFileSync(this.configPath, "utf-8");
      const lines = content.split("\n");
      let currentSection = "";
      const newConfig = {
        ipAllowlist: [],
        websiteAllowlist: [],
        portsAllowlist: []
      };
      for (const line of lines) {
        const trimmed = line.trim();
        if (!trimmed || trimmed.startsWith("#")) continue;
        if (trimmed.startsWith("[") && trimmed.endsWith("]")) {
          currentSection = trimmed.slice(1, -1).toLowerCase();
          continue;
        }
        switch (currentSection) {
          case "ip_allowlist":
            newConfig.ipAllowlist.push(trimmed);
            break;
          case "website_allowlist":
            newConfig.websiteAllowlist.push(trimmed);
            break;
          case "ports_allowlist":
            newConfig.portsAllowlist.push(trimmed);
            break;
        }
      }
      if (newConfig.ipAllowlist.length > 0) {
        this.config.ipAllowlist = newConfig.ipAllowlist;
      }
      if (newConfig.websiteAllowlist.length > 0) {
        this.config.websiteAllowlist = newConfig.websiteAllowlist;
      }
      if (newConfig.portsAllowlist.length > 0) {
        this.config.portsAllowlist = newConfig.portsAllowlist;
      }
      this.parseIPNetworks();
      console.log("[AllowlistManager] Loaded config:", {
        ips: this.config.ipAllowlist.length,
        websites: this.config.websiteAllowlist.length,
        ports: this.config.portsAllowlist.length
      });
    } catch (error) {
      console.error("[AllowlistManager] Error loading config:", error);
    }
  }
  parseIPNetworks() {
    this.ipNets = [];
    for (const entry of this.config.ipAllowlist) {
      if (entry.includes("/")) {
        const [network, maskStr] = entry.split("/");
        const mask = parseInt(maskStr, 10);
        this.ipNets.push({ network, mask });
      } else {
        const mask = entry.includes(":") ? 128 : 32;
        this.ipNets.push({ network: entry, mask });
      }
    }
  }
  watchConfig() {
    if (!existsSync(this.configPath)) return;
    watchFile(this.configPath, { interval: 5e3 }, () => {
      console.log("[AllowlistManager] Config file changed, reloading...");
      this.loadConfig();
    });
  }
  /**
   * Check if an IP address is allowed
   */
  isIPAllowed(ip) {
    if (this.config.ipAllowlist.includes("0.0.0.0/0") || this.config.ipAllowlist.includes("::0/0")) {
      return true;
    }
    const normalizedIP = this.normalizeIP(ip);
    for (const { network, mask } of this.ipNets) {
      if (this.isIPInNetwork(normalizedIP, network, mask)) {
        return true;
      }
    }
    return false;
  }
  /**
   * Check if a website/domain is allowed
   */
  isWebsiteAllowed(url) {
    let domain = url;
    try {
      if (url.startsWith("http://") || url.startsWith("https://")) {
        const urlObj = new URL(url);
        domain = urlObj.hostname;
      }
    } catch {
    }
    if (this.yamlConfigManager) {
      return this.yamlConfigManager.shouldAllowDomain(domain);
    }
    domain = domain.replace(/^www\./, "");
    for (const allowed of this.config.websiteAllowlist) {
      const allowedDomain = allowed.replace(/^www\./, "");
      if (domain === allowedDomain || domain.endsWith("." + allowedDomain)) {
        return true;
      }
    }
    return false;
  }
  /**
   * Check if a port is allowed (if port restrictions are configured)
   */
  isPortAllowed(port) {
    if (this.config.portsAllowlist.length === 0) {
      return true;
    }
    for (const entry of this.config.portsAllowlist) {
      if (entry.includes("-")) {
        const [start, end] = entry.split("-").map((p) => parseInt(p, 10));
        if (port >= start && port <= end) {
          return true;
        }
      } else {
        if (port === parseInt(entry, 10)) {
          return true;
        }
      }
    }
    return false;
  }
  /**
   * Get all allowed websites (for Docker configuration)
   */
  getAllowedWebsites() {
    return [...this.config.websiteAllowlist];
  }
  /**
   * Get allowed IPs for display/debugging
   */
  getAllowedIPs() {
    return [...this.config.ipAllowlist];
  }
  /**
   * Normalize IP address (handle IPv6 variations)
   */
  normalizeIP(ip) {
    if (ip.startsWith("::ffff:")) {
      return ip.substring(7);
    }
    return ip;
  }
  /**
   * Check if IP is in network (simplified, production should use proper IP library)
   */
  isIPInNetwork(ip, network, mask) {
    if (network === "0.0.0.0" && mask === 0) {
      return !ip.includes(":");
    }
    if (network === "::" && mask === 0) {
      return ip.includes(":");
    }
    if (mask === 32 && !ip.includes(":") || mask === 128 && ip.includes(":")) {
      return ip === network;
    }
    if (!ip.includes(":") && !network.includes(":")) {
      const ipParts = ip.split(".").map((p) => parseInt(p, 10));
      const netParts = network.split(".").map((p) => parseInt(p, 10));
      if (ipParts.length !== 4 || netParts.length !== 4) return false;
      const ipNum = ipParts[0] << 24 | ipParts[1] << 16 | ipParts[2] << 8 | ipParts[3];
      const netNum = netParts[0] << 24 | netParts[1] << 16 | netParts[2] << 8 | netParts[3];
      const maskNum = 4294967295 << 32 - mask >>> 0;
      return (ipNum & maskNum) === (netNum & maskNum);
    }
    return ip === network;
  }
  /**
   * Reload configuration manually
   */
  reload() {
    this.loadConfig();
  }
  /**
   * Cleanup resources
   */
  destroy() {
    if (this.fileWatcher) {
      clearInterval(this.fileWatcher);
    }
  }
}
const allowlistManager = new AllowlistManager();
let env = process.env;
try {
  if (typeof process.env.VITE !== "undefined") {
    const envModule = await import("./private.js").then((n) => n._);
    env = envModule.env;
  }
} catch {
  env = process.env;
}
function getAuthConfig() {
  const mode = env.MORPHBOX_AUTH_MODE || "none";
  const enabled = mode === "external" || mode === "vpn" && env.MORPHBOX_AUTH_ENABLED === "true";
  return {
    enabled,
    mode,
    token: env.MORPHBOX_AUTH_TOKEN,
    username: env.MORPHBOX_AUTH_USERNAME,
    password: env.MORPHBOX_AUTH_PASSWORD
  };
}
function generateAuthToken() {
  return crypto.randomBytes(32).toString("hex");
}
function validateToken(token) {
  const config = getAuthConfig();
  if (!config.enabled) {
    return true;
  }
  if (!token) {
    return false;
  }
  const storedToken = config.token || env.MORPHBOX_AUTH_TOKEN;
  if (!storedToken) {
    return false;
  }
  if (token === "authenticated" && config.username && config.password) {
    return true;
  }
  try {
    return crypto.timingSafeEqual(
      Buffer.from(token),
      Buffer.from(storedToken)
    );
  } catch {
    return false;
  }
}
function validateBasicAuth(username, password) {
  const config = getAuthConfig();
  if (!config.enabled) {
    return true;
  }
  if (!username || !password || !config.username || !config.password) {
    return false;
  }
  const usernameMatch = crypto.timingSafeEqual(
    Buffer.from(username),
    Buffer.from(config.username)
  );
  const passwordMatch = crypto.timingSafeEqual(
    Buffer.from(password),
    Buffer.from(config.password)
  );
  return usernameMatch && passwordMatch;
}
function extractAuthCredentials(event) {
  const token = event.url.searchParams.get("token") || event.request.headers.get("x-auth-token") || event.cookies.get("morphbox-auth-token");
  const authHeader = event.request.headers.get("authorization");
  let username;
  let password;
  if (authHeader?.startsWith("Basic ")) {
    const base64Credentials = authHeader.slice(6);
    const credentials = Buffer.from(base64Credentials, "base64").toString("utf-8");
    const [user, pass] = credentials.split(":");
    username = user;
    password = pass;
  }
  return { token, username, password };
}
function isAuthenticated(event) {
  const config = getAuthConfig();
  if (!config.enabled) {
    return true;
  }
  const clientIp = event.getClientAddress();
  if (!allowlistManager.isIPAllowed(clientIp)) {
    console.warn(`[Auth] Blocked connection from non-allowlisted IP: ${clientIp}`);
    return false;
  }
  if (authRateLimiter.isBlocked(clientIp)) {
    console.warn(`[Auth] Blocked authentication attempt from ${clientIp} due to rate limiting`);
    return false;
  }
  const { token, username, password } = extractAuthCredentials(event);
  if (config.token && validateToken(token || null)) {
    authRateLimiter.recordSuccessfulAttempt(clientIp);
    return true;
  }
  if (config.username && config.password && validateBasicAuth(username || "", password || "")) {
    authRateLimiter.recordSuccessfulAttempt(clientIp);
    return true;
  }
  authRateLimiter.recordFailedAttempt(clientIp);
  const remainingAttempts = authRateLimiter.getRemainingAttempts(clientIp);
  console.warn(`[Auth] Failed authentication from ${clientIp}. ${remainingAttempts} attempts remaining`);
  return false;
}
function getLoginPageHTML() {
  return `
<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Morphbox - Authentication Required</title>
  <style>
    body {
      margin: 0;
      padding: 0;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      background: #1a1a1a;
      color: #fff;
      display: flex;
      align-items: center;
      justify-content: center;
      height: 100vh;
    }
    .login-container {
      background: #2a2a2a;
      padding: 2rem;
      border-radius: 8px;
      box-shadow: 0 4px 6px rgba(0, 0, 0, 0.3);
      width: 100%;
      max-width: 400px;
    }
    h1 {
      margin: 0 0 1.5rem;
      font-size: 1.5rem;
      text-align: center;
    }
    .warning {
      background: #ff4444;
      color: #fff;
      padding: 0.75rem;
      border-radius: 4px;
      margin-bottom: 1.5rem;
      font-size: 0.875rem;
    }
    form {
      display: flex;
      flex-direction: column;
      gap: 1rem;
    }
    label {
      display: flex;
      flex-direction: column;
      gap: 0.25rem;
    }
    input {
      padding: 0.5rem;
      border: 1px solid #444;
      border-radius: 4px;
      background: #1a1a1a;
      color: #fff;
      font-size: 1rem;
    }
    button {
      padding: 0.75rem;
      border: none;
      border-radius: 4px;
      background: #0e639c;
      color: #fff;
      font-size: 1rem;
      cursor: pointer;
      transition: background 0.2s;
    }
    button:hover {
      background: #1177bb;
    }
    .error {
      color: #ff6666;
      font-size: 0.875rem;
      margin-top: 0.5rem;
    }
  </style>
</head>
<body>
  <div class="login-container">
    <h1>🔐 Morphbox Authentication</h1>
    <div class="warning">
      ⚠️ <strong>Security Notice:</strong> You are accessing Morphbox in external mode. 
      Authentication is required to protect your system.
    </div>
    <form method="POST" action="/api/auth/login">
      <label>
        Username:
        <input type="text" name="username" required autofocus>
      </label>
      <label>
        Password:
        <input type="password" name="password" required>
      </label>
      <button type="submit">Login</button>
    </form>
  </div>
</body>
</html>
  `;
}
export {
  generateAuthToken as a,
  getLoginPageHTML as b,
  getAuthConfig as g,
  isAuthenticated as i,
  validateBasicAuth as v
};
